/**
 * Entries on the phone — search, a week at a time, grouped by day.
 *
 * The tracker's mobile Entries screen: a search field wide enough to use, a
 * week pager, filter chips that scroll sideways rather than wrapping into three
 * rows, and day headings carrying that day's total. Tapping a row opens the
 * same sheet the + button does, because correcting an entry away from a desk is
 * the whole reason this screen is on a phone. Rows continue (▶ or swipe right)
 * and delete (swipe left, with undo).
 *
 * Day shows one day of the week as a timeline instead, so gaps — and, with a
 * calendar connected, meetings nobody logged — are visible at a glance.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useChrome } from "@/context/ChromeContext";
import { useAllItems, useProjects } from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import {
  breakdownText, clock, deriveEntryLabel, entryDayKey, groupBy, hours, inBounds, entryKind, isUnbilled, periodBounds,
  projectName, toIsoLocal, totalsOf,
} from "@/lib/timeRollup";
import type { TimeEntry } from "@/lib/types";

import { useContinueEntry } from "@/hooks/useContinueEntry";

import { SectionLabel, Segmented, field, screen } from "../components";
import { DayTimeline } from "../components/DayTimeline";
import { useLogSheet } from "../components/LogSheetContext";
import { EntryRow, useDeleteEntry } from "./MobileToday";

export function MobileEntries() {
  const logSheet = useLogSheet();
  const continueEntry = useContinueEntry();
  const deleteEntry = useDeleteEntry();
  const [view, setView] = useState<"list" | "day">("list");
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();

  const [offset, setOffset] = useState(0);
  /**
   * Open on the week of the most recent entry, not on this one.
   *
   * This lived in useState's initialiser, which runs on the FIRST render —
   * before React Query has resolved, when `entries` is still empty. So it
   * always took the empty branch and always opened on the current week, which
   * on a Monday morning is a blank screen with your work one tap away and no
   * sign of it. Runs once, when the data actually arrives.
   */
  const [homed, setHomed] = useState(false);
  useEffect(() => {
    if (homed || entries.length === 0) return;
    const latest = entries.reduce((m, e) => (e.start > m ? e.start : m), entries[0].start);
    const weeksBack = Math.round(
      (periodBounds("week", 0).start.getTime() - periodBounds("week", 0, new Date(latest)).start.getTime())
      / (7 * 86_400_000),
    );
    setHomed(true);
    if (weeksBack > 0) setOffset(-weeksBack);
  }, [entries, homed]);
  /** Insights links here with a project or activity — see MobileInsights. */
  const [params, setParams] = useSearchParams();
  const [allTime, setAllTime] = useState(params.get("all") === "1");
  const [projectId, setProjectId] = useState(params.get("project") ?? "all");
  const [activity, setActivity] = useState(params.get("activity") ?? "all");
  useEffect(() => {
    if (params.toString()) setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [billableOnly, setBillableOnly] = useState(false);
  const [unbilledOnly, setUnbilledOnly] = useState(false);
  const [query, setQuery] = useState("");

  const week = useMemo(() => periodBounds("week", offset), [offset]);
  const itemTitle = useMemo(() => new Map(items.map((i) => [i.id, i.title])), [items]);
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const activityOptions = useMemo(() => {
    const seen = new Set(entries.map((e) => e.category).filter(Boolean) as string[]);
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (!allTime && !inBounds(e, week)) return false;
      if (projectId === "none" ? e.project : projectId !== "all" && e.project !== projectId) return false;
      if (activity === "none" ? e.category : activity !== "all" && e.category !== activity) return false;
      if (billableOnly && entryKind(e, projects) !== "hourly") return false;
      if (unbilledOnly && !isUnbilled(e, projects)) return false;
      if (!q) return true;
      return `${e.note ?? ""} ${e.category ?? ""} ${projectName(projects, e.project)}`.toLowerCase().includes(q);
    });
  }, [entries, allTime, week, projectId, activity, billableOnly, unbilledOnly, query, projects]);

  const totals = totalsOf(filtered, projects);
  const byDay = useMemo(() => {
    const map = groupBy(filtered, entryDayKey);
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([day, rows]) => ({
        day,
        rows: [...rows].sort((a, b) => (b.start || "").localeCompare(a.start || "")),
        totals: totalsOf(rows, projects),
      }));
  }, [filtered]);

  useChrome({
    title: "Entries",
    subtitle: `${filtered.length} · ${hours(totals.seconds)} h${allTime ? "" : ` · ${week.label}`}`,
  }, [filtered.length, totals.seconds, allTime, week.label]);

  const today = toIsoLocal(new Date());

  // The seven days of the week on screen; Day opens on today when it's in
  // that week, otherwise on the last day that has anything logged.
  const weekDays = useMemo(() => {
    const out: string[] = [];
    const d = new Date(week.start);
    for (let i = 0; i < 7; i++) { out.push(toIsoLocal(d)); d.setDate(d.getDate() + 1); }
    return out;
  }, [week]);
  const day = pickedDay && weekDays.includes(pickedDay)
    ? pickedDay
    : weekDays.includes(today) ? today : byDay[0]?.day && weekDays.includes(byDay[0].day) ? byDay[0].day : weekDays[0];

  return (
    <div style={screen}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => { setAllTime(false); setOffset((o) => o - 1); }} aria-label="Previous week" style={pager}>‹</button>
        <button
          onClick={() => { setAllTime((v) => !v); setOffset(0); }}
          className="mono"
          style={{
            flex: 1, minHeight: 44, borderRadius: 9, cursor: "pointer", fontSize: 13.5,
            border: "1px solid var(--line2)",
            background: allTime ? "var(--accentTint)" : "var(--surface)",
            color: allTime ? "var(--accent)" : "var(--ink)",
          }}
        >{allTime ? "All time" : week.label}</button>
        <button
          onClick={() => { setAllTime(false); setOffset((o) => Math.min(0, o + 1)); }}
          disabled={!allTime && offset >= 0}
          aria-label="Next week"
          style={{ ...pager, opacity: !allTime && offset >= 0 ? 0.35 : 1 }}
        >›</button>
      </div>

      <div style={{ marginTop: 8 }}>
        <Segmented
          value={view}
          onChange={(v) => { setView(v); if (v === "day") setAllTime(false); }}
          options={[{ value: "list", label: "List" }, { value: "day", label: "Day" }]}
        />
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search entries"
        aria-label="Search entries"
        style={{ ...field, marginTop: 8 }}
      />

      {/* Chips scroll sideways rather than wrapping — three rows of filters
          above a list is most of a phone screen spent not showing the list. */}
      <div style={{
        display: "flex", gap: 7, marginTop: 8, overflowX: "auto",
        paddingBottom: 2, WebkitOverflowScrolling: "touch",
      }}>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Filter by project"
          style={{ ...chip, ...(projectId !== "all" ? chipOn : null), maxWidth: 190 }}
        >
          <option value="all">All projects</option>
          <option value="none">Ad-hoc</option>
          {projects.filter((p) => p.kind !== "inbox").map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        {activityOptions.length > 0 && (
          <select
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
            aria-label="Filter by activity"
            style={{ ...chip, ...(activity !== "all" ? chipOn : null), maxWidth: 170 }}
          >
            <option value="all">All activities</option>
            <option value="none">No activity</option>
            {activityOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}

        <button
          onClick={() => setBillableOnly((v) => !v)}
          style={{ ...chip, ...(billableOnly ? chipOn : null) }}
        >{billableOnly ? "✓ Billable" : "Billable"}</button>

        <button
          onClick={() => setUnbilledOnly((v) => !v)}
          style={{ ...chip, ...(unbilledOnly ? chipOn : null) }}
        >{unbilledOnly ? "✓ Not invoiced" : "Not invoiced"}</button>
      </div>

      {view === "day" ? (
        <>
          <div style={{ display: "flex", gap: 4, marginTop: 10 }}>
            {weekDays.map((d) => {
              const on = d === day;
              const date = new Date(`${d}T12:00:00`);
              const has = byDay.some((g) => g.day === d);
              return (
                <button
                  key={d}
                  onClick={() => setPickedDay(d)}
                  aria-pressed={on}
                  style={{
                    flex: 1, minHeight: 50, borderRadius: 10, cursor: "pointer", padding: 0,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                    border: `1px solid ${on ? "var(--accentLine)" : "var(--line2)"}`,
                    background: on ? "var(--accentTint)" : "var(--surface)",
                    color: on ? "var(--accent)" : d === today ? "var(--ink)" : "var(--ink3)",
                  }}
                >
                  <span style={{ fontSize: 10.5 }}>{date.toLocaleDateString(undefined, { weekday: "narrow" })}</span>
                  <span className="mono" style={{ fontSize: 14, fontWeight: on || d === today ? 600 : 400 }}>{date.getDate()}</span>
                  <span style={{ width: 4, height: 4, borderRadius: "50%", background: has ? "currentColor" : "transparent" }} />
                </button>
              );
            })}
          </div>
          <SectionLabel right={(() => { const g = byDay.find((x) => x.day === day); return g ? `${clock(g.totals.seconds)}` : "0:00"; })()}>
            {day === today ? "Today" : dayLabel(day)}
          </SectionLabel>
          <DayTimeline
            day={day}
            entries={filtered}
            projects={projects}
            items={items}
            onEdit={(e) => logSheet.open({ entry: e })}
            onLogEvent={(ev) => logSheet.open({
              projectId: ev.projectId || undefined, note: ev.summary, start: ev.start, minutes: ev.durationMinutes,
            })}
          />
        </>
      ) : byDay.length === 0 ? (
        <div style={{ padding: "40px 2px", textAlign: "center", color: "var(--muted)", fontSize: 13.5 }}>
          No entries match these filters.
        </div>
      ) : (
        byDay.map(({ day, rows, totals: dayTotals }) => (
          <div key={day} style={{ marginBottom: 10 }}>
            <SectionLabel right={`${clock(dayTotals.seconds)} · ${breakdownText(dayTotals, clock)}`}>
              {day === today ? "Today" : dayLabel(day)}
            </SectionLabel>
            {rows.map((e: TimeEntry) => (
              <EntryRow
                key={e.id}
                entry={e}
                color={projectById.get(e.project ?? "")?.color}
                label={deriveEntryLabel(e.note, itemTitle.get(e.item ?? ""))}
                project={projectById.get(e.project ?? "")?.name}
                kind={entryKind(e, projects)}
                onEdit={() => logSheet.open({ entry: e })}
                onContinue={() => void continueEntry(e)}
                onDelete={() => deleteEntry(e)}
              />
            ))}
          </div>
        ))
      )}

      {/* Wraps: four figures side by side ran 40px past a 375pt screen, which
          let the whole page slide sideways under your thumb. */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "6px 14px",
        marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)",
      }}>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Week starts Monday</span>
        <span style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", justifyContent: "flex-end", marginLeft: "auto" }}>
          <Figure label="Billable" value={clock(totals.billableSeconds)} />
          {totals.projectSeconds > 0 && <Figure label="Project" value={clock(totals.projectSeconds)} />}
          {totals.internalSeconds > 0 && <Figure label="Internal" value={clock(totals.internalSeconds)} />}
          <Figure label="Total" value={clock(totals.seconds)} />
        </span>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
      <span style={{ fontSize: 11.5, color: "var(--muted)" }}>{label}</span>
      <span className="mono" style={{ fontSize: 16, fontWeight: 600 }}>{value}</span>
    </span>
  );
}

/** "Tue · Sep 15" */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return `${date.toLocaleDateString(undefined, { weekday: "short" })} · ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

const pager: React.CSSProperties = {
  flex: "none", width: 44, minHeight: 44, borderRadius: 9, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 16,
};
const chip: React.CSSProperties = {
  flex: "none", minHeight: 38, padding: "0 12px", borderRadius: 999, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
  fontSize: 13, whiteSpace: "nowrap",
};
const chipOn: React.CSSProperties = {
  background: "var(--accentTint)", border: "1px solid var(--accentLine)", color: "var(--accent)",
};
